<template>
  <b-card title="News">
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label>Show</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <b-button variant="primary" :to="{ name: 'admin-news-add' }">
            Create News
          </b-button>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
        >
          <label class="mr-1">
            Search
          </label>
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              class="d-inline-block mr-1"
              placeholder="Search..."
              v-model="searchWord"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <!-- Larger screen than mobile -->
    <div class="d-none d-md-flex">
      <!-- Table -->
      <b-table responsive="sm" :items="news" :fields="fields" :busy="isBusy">
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
            <strong class="text-secondary">Loading...</strong>
          </div>
        </template>
        <template #cell(action)="news">
          <div class="d-flex justify-content-start">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="secondary"
              class="btn-icon mr-1"
              :to="{ name: 'admin-news-edit', params: { id: news.item.id } }"
            >
              <feather-icon icon="Edit3Icon" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="danger"
              class="btn-icon"
              @click="confirmDeleteNews(news.item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
        <template #cell(description)="news">
          <div :inner-html.prop="news.item.content | truncate(250)"></div>
        </template>
        <template #cell(published_at)="news">
          <div>
            {{
              news.item.published_at
                ? $moment(news.item.published_at).format('dddd, Do MMMM YYYY')
                : '-'
            }}
          </div>
        </template>
      </b-table>
    </div>
    <!-- Mobile screen -->
    <div class="d-flex d-md-none flex-column">
      <!-- Loading -->
      <div v-if="isBusy" class="text-center text-danger my-2">
        <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
        <strong class="text-secondary">Loading...</strong>
      </div>
      <!-- Data -->
      <b-row
        v-if="!isBusy"
        v-for="(item, index) in news"
        :key="item.id"
        class="mb-3 list-item"
      >
        <b-col cols="12" class="d-flex flex-wrap justify-content-end align-items-center mb-1" style="gap: 1rem;">
          <strong class="flex-grow-1 truncate">{{ item.title }}</strong>
          <div>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="secondary"
              class="btn-icon mr-1"
              :to="{ name: 'admin-news-edit', params: { id: item.id } }"
            >
              <feather-icon icon="Edit3Icon" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="danger"
              class="btn-icon"
              @click="confirmDeleteNews(item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </b-col>
        <b-col cols="12" class="d-flex flex-wrap">
          <div style="font-size: 0.75rem;">
            <p><strong>PUBLISHED AT:</strong> {{ item.published_at ? $moment(item.published_at).format('dddd, Do MMMM YYYY') : '-' }}</p>
            <p><strong>DESCRIPTION:</strong> <span :inner-html.prop="item.content | truncate(250)"></span></p>
          </div>
        </b-col>
      </b-row>
    </div>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">
            Showing {{ pagination.from }} to {{ pagination.to }} of
            {{ pagination.total }} entries
          </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="pagination.total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
            @input="changePage"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
  BSpinner,
  BFormCheckbox,
  BModal,
  BImg,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,
    BSpinner,
    BFormCheckbox,
    BModal,
    BImg,

    vSelect,
  },
  data() {
    return {
      isBusy: false,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 20, 30],
      pagination: {
        from: 1,
        to: 10,
        total: 0,
      },
      fields: [
        { key: 'title', sortable: true },
        'published_at',
        'description',
        'action',
      ],
      news: [],
      searchWord: '',
    }
  },

  directives: {
    Ripple,
  },

  watch: {
    perPage() {
      this.fetchNews()
    },
    currentPage() {
      this.fetchNews()
    },
    searchWord() {
      this.fetchNews()
    },
  },

  filters: {
    truncate: function(value, length, start = 0) {
      if (!value) return ''
      value = value.toString()
      if (value.length > length) {
        if (start > 0) {
          return value.substring(start, length + start) + '...'
        } else {
          return value.substring(start, length) + '...'
        }
      } else {
        return value
      }
    },
  },

  mounted() {
    this.fetchNews()
  },

  methods: {
    fetchNews() {
      this.isBusy = true

      this.$http
        .get('/news', {
          params: {
            page: this.currentPage,
            perPage: this.perPage,
            s: this.searchWord.toString().trim(),
          },
        })
        .then(({ data }) => {
          this.currentPage = data.current_page
          this.pagination.total = data.total
          this.pagination.from = data.from
          this.pagination.to = data.to
          this.news = data.data
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    changePage(page) {
      this.currentPage = page
    },
    async deleteNews(newsId) {
      this.isBusy = true
      return await this.$http
        .delete(`/news/${newsId}`)
        .then(res => {
          this.$notif(
            'Success',
            'News has been successfully deleted.',
            'success'
          )
        })
        .catch(({ data }) => {
          this.$notif('Something Error', data.message, 'danger')
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    confirmDeleteNews(newsId) {
      this.$bvModal
        .msgBoxConfirm('Please confirm that you want to delete this item.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.deleteNews(newsId).then(() => {
              this.fetchNews()
            })
          }
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
